/**
 * AI Event Tracker — Unified AI Observability (PLAT-9155)
 *
 * Single abstraction for tracking AI operation lifecycles (start → complete/fail).
 * Fans out to all configured backends (PostHog, and extensible to Datadog, etc.)
 * so callers never import individual integrations.
 *
 * Usage:
 *   const tracker = AIEventTracker.start('ai_content_generation', { provider, model_id });
 *   try {
 *     // ... do work ...
 *     tracker.complete({ extra: 'value' });
 *   } catch (err) {
 *     tracker.fail(err);
 *     throw err;
 *   }
 */

import { PostHogIntegration } from './PostHogIntegration';

export type AIEventProperties = Record<string, unknown>;

class AIEventTrackerInstance {
  private eventPrefix: string;
  private properties: AIEventProperties;
  private startTime: number;
  private ended = false;

  constructor(eventPrefix: string, properties: AIEventProperties) {
    this.eventPrefix = eventPrefix;
    this.properties = properties;
    this.startTime = Date.now();
    this.emit('started', properties);
  }

  complete(extra?: AIEventProperties): void {
    if (this.ended) return;
    this.ended = true;
    this.emit('completed', {
      ...this.properties,
      ...extra,
      latency_ms: Date.now() - this.startTime,
    });
  }

  fail(error: unknown, extra?: AIEventProperties): void {
    if (this.ended) return;
    this.ended = true;
    this.emit('failed', {
      ...this.properties,
      ...extra,
      error_message:
        error instanceof Error ? error.message : String(error),
      latency_ms: Date.now() - this.startTime,
    });
  }

  private emit(suffix: string, properties: AIEventProperties): void {
    const event = `${this.eventPrefix}_${suffix}`;
    // Fan-out to all configured backends
    PostHogIntegration.captureAIEvent(event, properties);
  }
}

export const AIEventTracker = {
  start(
    eventPrefix: string,
    properties: AIEventProperties = {},
  ): AIEventTrackerInstance {
    return new AIEventTrackerInstance(eventPrefix, properties);
  },
};
